/**
 * MessageScopeChips — read-only chip row rendered under a user message
 * bubble to surface the sticky scope (folders + files) that was applied
 * to that turn. Jun 2026 user feedback — without this surface, the user
 * couldn't tell after the fact WHICH file they'd asked the agent to
 * focus on. Mirrors the visual family of ``Composer.FileChipList``
 * (same horizontal pill anatomy) so the bubble's "attached / scoped"
 * affordances read as one cohesive group.
 *
 * Distinct from ``FileChipList`` (files the user *uploaded* into the
 * chat): these are files/folders the user *pointed at* via the
 * SourceScopePicker or inline ``@``-mention. No remove action — the
 * chips are historical.
 */

import { FileText, Folder, Plug, Users } from "lucide-react";
import type * as React from "react";

import { cn } from "../lib/cn";
import { scopeDocSourceLabel } from "./composer-parts/scope-shared";

export type MessageScopeChipKind =
  | "document"
  | "folder"
  | "connector"
  | "person"
  | "thread";

export interface MessageScopeChip {
  readonly kind: MessageScopeChipKind;
  readonly id: string;
  readonly display: string;
  /** White-label source slug, drives the corner badge label. */
  readonly provider?: string;
  /** Folder path inside the source — shown muted under the display. */
  readonly folderPath?: string;
}

export interface MessageScopeChipsProps {
  readonly chips: ReadonlyArray<MessageScopeChip>;
  /** Optional click handler — fires with the chip when the user taps it. */
  readonly onChipClick?: (chip: MessageScopeChip) => void;
  readonly className?: string;
}

const SCOPE_ICON_BY_KIND: Record<
  MessageScopeChipKind,
  React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
> = {
  document: FileText,
  folder: Folder,
  connector: Plug,
  person: Users,
  thread: FileText,
};

export const MessageScopeChips: React.FC<MessageScopeChipsProps> = ({
  chips,
  onChipClick,
  className,
}) => {
  if (chips.length === 0) return null;
  const clickable = onChipClick !== undefined;
  return (
    <ul
      aria-label="Ambito del messaggio"
      className={cn(
        "m-0 flex list-none flex-wrap items-center gap-1 p-0",
        className,
      )}
    >
      {chips.map((chip) => (
        <li key={`${chip.kind}:${chip.id}`} className="m-0 p-0">
          <ScopeChipBody
            chip={chip}
            onClick={
              clickable
                ? () => onChipClick(chip)
                : undefined
            }
          />
        </li>
      ))}
    </ul>
  );
};
MessageScopeChips.displayName = "MessageScopeChips";

// ── Single chip body ─────────────────────────────────────────────────────
//
// Renders as <button> ONLY when an onClick is provided (interactive
// affordance with hover + focus ring + keyboard). Otherwise renders as
// <div> with the same visuals — read-only chips are NOT focusable nor
// announced as "disabled button" by screen readers (WAI-ARIA APG: a
// non-interactive element shouldn't masquerade as a disabled control).

interface ScopeChipBodyProps {
  readonly chip: MessageScopeChip;
  readonly onClick?: () => void;
}

function ScopeChipBody({
  chip,
  onClick,
}: ScopeChipBodyProps): React.ReactElement {
  const Icon = SCOPE_ICON_BY_KIND[chip.kind] ?? FileText;
  const badge = scopeDocSourceLabel(chip.provider);
  const secondary = chip.folderPath;
  const ariaLabel =
    [chip.display, badge ?? undefined, secondary ?? undefined]
      .filter(Boolean)
      .join(" · ") || chip.display;
  const interactive = onClick !== undefined;

  const className = cn(
    "inline-flex max-w-[260px] items-center gap-1.5 rounded-full border border-hairline bg-white/55 px-2.5 py-1 text-[11px] text-ink-2 transition-all",
    interactive
      ? "hover:-translate-y-px cursor-pointer hover:border-ando-orange/30 hover:bg-white hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
      : "cursor-default",
  );

  const content = (
    <>
      <Icon aria-hidden className="h-3 w-3 shrink-0 text-ando-orange" />
      <span className="truncate font-medium text-ink-1">{chip.display}</span>
      {secondary ? (
        <span aria-hidden className="truncate text-[10px] text-ink-3">
          {secondary}
        </span>
      ) : null}
      {badge ? (
        <span
          aria-hidden
          className="shrink-0 rounded-md bg-ink-1/[0.06] px-1.5 py-px font-medium text-[9px] text-ink-2 uppercase tracking-wide"
        >
          {badge}
        </span>
      ) : null}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        title={chip.display}
        className={className}
      >
        {content}
      </button>
    );
  }
  return (
    <div aria-label={ariaLabel} title={chip.display} className={className}>
      {content}
    </div>
  );
}
